import { useState } from 'react';
import { Popover } from '@mantine/core';

import { showToast } from 'utils/utility';
import { useFlightData } from 'libs/data-access';
import { IFlightOnlineBookingList } from 'libs/data-access/types';
import { EButton, ECheckbox, EPopover, ETextArea } from 'libs/ui';


interface ISelectedSegment {
    bookingId: number;
    journeyKey: string;
    type: 'galileo' | 'navitaire' | 'akbar';
    origin: string;
    destination: string;
    locatorCode: string | null;
};

interface IProps {
    flightTicket: IFlightOnlineBookingList;
    handleRefresh: () => void;
};


export default function Cancel1GBooking({ ...props }: IProps) {

    const {
        postFlightData
    } = useFlightData();

    const [cancelBookingState, setCancelBookingState] = useState<{
        opened: boolean;
        selectedSegments: ISelectedSegment[];
        remark: string;
        confirmed: boolean;
        isSaving: boolean;
    }>({
        opened: false,
        selectedSegments: [],
        remark: '',
        confirmed: false,
        isSaving: false
    });


    // HANDLER FUNCTIONS
    const closePopover = () => setCancelBookingState({
        opened: false,
        selectedSegments: [],
        remark: '',
        confirmed: false,
        isSaving: false
    });

    const handleSegmentSelection = (segment: ISelectedSegment, checked: boolean) => {
        setCancelBookingState(prev => ({
            ...prev,
            selectedSegments: checked
                ? [...prev.selectedSegments, segment]
                : prev.selectedSegments.filter(s =>
                    s.bookingId !== segment.bookingId ||
                    s.journeyKey !== segment.journeyKey
                )
        }));
    };

    // helper function to get all segments
    const getAllSegments = () => {
        const segments: ISelectedSegment[] = [];

        props.flightTicket.bookingResponses.forEach(booking => {
            // Galileo segments
            booking.galileoBookingDetailResponses?.forEach(journey => {
                journey.bookingStatus === "SUCCESS" && segments.push({
                    bookingId: booking.id,
                    journeyKey: `${journey.origin}_${journey.destination}`,
                    type: 'galileo',
                    origin: journey.origin,
                    destination: journey.destination,
                    locatorCode: booking.locatorCode?.airReservationLocatorCode || null
                });
            });

            // Navitaire segments
            booking.navitaireBookingDetailResponses?.forEach(journey => {
                journey.bookingStatus === "SUCCESS" && segments.push({
                    bookingId: booking.id,
                    journeyKey: `${journey.origin}_${journey.destination}`,
                    type: 'navitaire',
                    origin: journey.origin,
                    destination: journey.destination,
                    locatorCode: booking.pnrDetail?.supplierPnr || null
                });
            });

            // akbar segments
            booking.akbarBookingDetailResponses?.forEach(journey => {
                journey.bookingStatus === "SUCCESS" && segments.push({
                    bookingId: booking.id,
                    journeyKey: `${journey.origin}_${journey.destination}`,
                    type: 'akbar',
                    origin: journey.origin,
                    destination: journey.destination,
                    locatorCode: booking.pnrDetail?.supplierPnr || null
                });
            });
        });

        return segments;
    };

    // API CALLS
    const cancelBooking = async () => {
        if (cancelBookingState.selectedSegments.length === 0) {
            showToast({
                time: 5 * 1000,
                type: "warning",
                message: "Please select at least one segment to cancel!"
            });
            return;
        };

        if (!cancelBookingState.remark) {
            showToast({
                type: "warning",
                message: "Please provide a remark for cancellation!"
            });
            return;
        };

        setCancelBookingState(prev => ({ ...prev, isSaving: true }));

        // console.log(cancelBookingState);

        for (const segment of cancelBookingState.selectedSegments) {
            const endpoint = segment.type === 'galileo'
                ? `/booking/${segment.bookingId}/cancel-booking?number=${segment.locatorCode}&key=${segment.journeyKey}&remark=${cancelBookingState.remark}`
                : `/booking/${segment.bookingId}/cancel-booking?number=${segment.locatorCode}&key=${segment.journeyKey}&remark=${cancelBookingState.remark}`;

            const response = await postFlightData(endpoint, null);

            if (response.isSuccess) {
                showToast({
                    type: "success",
                    message: `Segment ${segment.origin}-${segment.destination} cancelled successfully!`
                });
                alert("For the final cancellation, please contact the admin now.");
                return;
            } else {
                console.log("Error for cancel segment", response);
                showToast({
                    type: "error",
                    message: response.message || `Error while cancelling segment ${segment.origin}-${segment.destination}!`
                });
            };
        };

        closePopover();
        props.handleRefresh();
        setCancelBookingState(prev => ({ ...prev, isSaving: false }));
    };


    return (
        <EPopover
            width={450}
            opened={cancelBookingState.opened}
            onClose={closePopover}
        >
            <Popover.Target>
                <div>
                    <EButton
                        size="sm"
                        variant="light"
                        color="orange"
                        onClick={() => setCancelBookingState(prev => ({ ...prev, opened: !prev.opened }))}
                    >Cancel</EButton>
                </div>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="w-96 p-2">
                    <h3 className="text-lg font-medium mb-4">Cancel Booking Segments</h3>
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-2">
                            {getAllSegments().map((segment, index) => (
                                <ECheckbox
                                    key={`${segment.bookingId}-${segment.journeyKey}`}
                                    label={<div>
                                        <span>{segment.origin} - {segment.destination}</span>
                                        {/* <span className="text-xs text-gray-400">({segment.type.toUpperCase()})</span> */}
                                    </div>}
                                    checked={cancelBookingState.selectedSegments.some(
                                        s => s.bookingId === segment.bookingId && s.journeyKey === segment.journeyKey
                                    )}
                                    onChange={(e) => handleSegmentSelection(segment, e.target.checked)}
                                />
                            ))}
                        </div>

                        <ETextArea
                            label="Cancellation Remark"
                            placeholder="Enter cancellation remark"
                            value={cancelBookingState.remark}
                            onChange={(e) => setCancelBookingState(prev => ({ ...prev, remark: e.target.value }))}
                        />

                        <ECheckbox
                            label="Are you sure you want to cancel the selected segments?"
                            checked={cancelBookingState.confirmed}
                            onChange={(e) => setCancelBookingState(prev => ({ ...prev, confirmed: e.target.checked }))}
                        />

                        {cancelBookingState.confirmed && (
                            <div className="flex justify-end gap-2">
                                <EButton
                                    variant="light"
                                    onClick={closePopover}
                                >Cancel</EButton>
                                <EButton
                                    color="red"
                                    loading={cancelBookingState.isSaving}
                                    onClick={cancelBooking}
                                >Confirm Cancel</EButton>
                            </div>
                        )}
                    </div>
                </div>
            </Popover.Dropdown>
        </EPopover>
    );
}